import { Injectable } from '@angular/core';
import { switchMap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { CrSyncService } from './crsync.service';

export interface LogoutOptions {
  /** Optional reason appended to the Portal login URL, e.g. 'logged_out_due_to_inactivity' */
  reason?: string;
}

/**
 * Single sign-out flow shared by the manual sign-out button and the automatic (idle) logout.
 *
 * Federated users (AD/IDP login, either site level or landing page) get their IDP session
 * closed by redirecting to the federation logout URL. Everyone else is sent to the Portal login.
 */
@Injectable({
  providedIn: 'root'
})
export class LogoutService {

  constructor(
    private api: ApiService,
    private crSync: CrSyncService
  ) { }

  logout(options: LogoutOptions = {}): void {
    const loginUrl = '/login' + (options.reason ? '?reason=' + encodeURIComponent(options.reason) : '');

    // Read login state before api.logout() clears local storage
    const user = this.readJson('user');
    // Check for possible landing page user
    const landingPageData = this.readJson('landing-page');
    const lpUser = landingPageData?.user;
    // Landing page users who logged in with username and password (non-federated) have no
    // identity provider, so there is no AD/IDP session to close for them
    const isFederatedLpUser = !!lpUser && !!landingPageData?.idProvider;
    // Different logout logic for authenticated (federated) users
    const isFederatedUser = !!(user && user.authenticated) || isFederatedLpUser;

    if (isFederatedUser) {
      this.api.logoutAuthenticatedUser(isFederatedLpUser).subscribe(
        (logoutUrl: string) => {
          // Redirect to the IDP logout URL (closes the federated session) or fall back to Portal login
          const finish = () => this.redirect(logoutUrl || loginUrl);
          this.api.logout().subscribe(finish, finish);
        },
        err => {
          console.error('logout authenticated user error', err);
          this.api.logout().subscribe(() => this.redirect(loginUrl));
        }
      );
      return;
    }

    // Non-federated landing page user: only holds a landing page token (no portal session to log),
    // so clear the session and go to the normal Portal login screen
    if (lpUser) {
      this.api.logout().subscribe(() => this.redirect(loginUrl));
      return;
    }

    // Log the logout event for non-authenticated users before logging out
    this.api.logLogoutEvent()
      .pipe(switchMap(() => this.api.logout()))
      .subscribe(
        () => this.redirect(loginUrl),
        // Proceed to logout even if logging fails
        () => this.api.logout().subscribe(() => this.redirect(loginUrl))
      );
  }

  private redirect(url: string): void {
    this.crSync.disconnect();
    this.navigate(url);
  }

  /** Separate method so tests can stub the full page navigation */
  navigate(url: string): void {
    window.location.href = url;
  }

  private readJson(key: string): any {
    try {
      return JSON.parse(localStorage.getItem(key));
    } catch {
      return null;
    }
  }
}
